/**
 * parse-blocks — a tolerant, line-oriented parser for Lunascript (`.ls`).
 *
 * This is NOT the authoritative compiler (that is the Go `lsc` binary; see
 * vendor/lunascripts/LS-SPEC.md). It is a *display* parser: it turns a raw
 * `.ls` source string into a flat, ordered `LsBlock[]` so the block-mode
 * editor (§5.2 双模式编辑器) can render each beat as a typed, colored row.
 *
 * Design rules (mirroring §4.14 EditorBlock + LS-SPEC §3–§4):
 *  - One block per *significant* source line. Comments / blank lines / pure
 *    structural braces (`{` / `}`) are skipped, never emitted.
 *  - Tolerant by contract: this MUST NEVER throw. Anything it cannot classify
 *    becomes `{ kind: "unknown", raw }` so the round-trip to code mode is lossless.
 *  - Every block carries `raw` (the trimmed source line) + `depth` (brace nesting,
 *    so compound blocks — @choice/@option/@if/@gate/@phone — can indent in the
 *    block view) + parsed, kind-specific fields.
 *  - `&` concurrent-prefix directives parse identically to their `@` form, with
 *    `concurrent: true` recorded so the row can show the follower relationship.
 */

/** The kind tag of a parsed block. Mirrors the §4.14 directive families. */
export type LsBlockKind =
  | "episode"
  | "bg"
  | "char"
  | "dialogue"
  | "narration"
  | "cg"
  | "music"
  | "sfx"
  | "pause"
  | "choice"
  | "option"
  | "check"
  | "if"
  | "gate"
  | "affection"
  | "signal"
  | "trick"
  | "phone"
  | "phoneText"
  | "minigame"
  | "achievement"
  | "butterfly"
  | "param"
  | "unknown";

/** Fields shared by every block. */
interface LsBlockBase {
  /** The original (trimmed) source line. Source of truth for round-trips. */
  readonly raw: string;
  /** 1-based source line number. */
  readonly line: number;
  /** Brace-nesting depth at this line (0 = top level / file root). */
  readonly depth: number;
  /** True when the directive used the `&` concurrent prefix (follower). */
  readonly concurrent?: boolean;
}

/** `@episode <branch_key> "<title>" {` — the file root. */
export interface EpisodeBlock extends LsBlockBase {
  readonly kind: "episode";
  readonly branchKey?: string;
  readonly title?: string;
}

/** `@bg set <name> [transition]`. */
export interface BgBlock extends LsBlockBase {
  readonly kind: "bg";
  readonly name?: string;
  readonly transition?: string;
}

/**
 * `@<char> <pose> [transition]` or `@<char> bubble <type>` — a character
 * presentation directive (立绘 pose / 气泡 bubble).
 */
export interface CharBlock extends LsBlockBase {
  readonly kind: "char";
  readonly character: string;
  /** pose name, or the bubble type when `bubble` is true. */
  readonly pose?: string;
  /** Optional transition (dissolve / fade / cut / slow). pose form only. */
  readonly transition?: string;
  /** True for the `@<char> bubble <type>` form. */
  readonly bubble?: boolean;
}

/** `CHARACTER: text` or `CHARACTER [pose]: text` — spoken dialogue. */
export interface DialogueBlock extends LsBlockBase {
  readonly kind: "dialogue";
  readonly speaker: string;
  readonly text: string;
  /** Inline pose-sugar: `CHARACTER [pose]: text`. */
  readonly pose?: string;
}

/** `NARRATOR: text` / `YOU: text` — un-attributed narration / inner monologue. */
export interface NarrationBlock extends LsBlockBase {
  readonly kind: "narration";
  /** "NARRATOR" (旁白) or "YOU" (内心独白). */
  readonly speaker: "NARRATOR" | "YOU";
  readonly text: string;
}

/** `@cg <name> "<content>"` — full-screen CG (leaf). */
export interface CgBlock extends LsBlockBase {
  readonly kind: "cg";
  readonly name?: string;
  readonly content?: string;
}

/** `@music <name>` / `@music stop`. */
export interface MusicBlock extends LsBlockBase {
  readonly kind: "music";
  readonly name?: string;
  /** True for `@music stop`. */
  readonly stop?: boolean;
}

/** `@sfx <name>`. */
export interface SfxBlock extends LsBlockBase {
  readonly kind: "sfx";
  readonly name?: string;
}

/** `@pause` — a tap-to-continue hold. */
export interface PauseBlock extends LsBlockBase {
  readonly kind: "pause";
}

/** `@choice {` — opens a forced-choice compound block. */
export interface ChoiceBlock extends LsBlockBase {
  readonly kind: "choice";
}

/** `@option <ID> <brave|safe> "<text>" {`. */
export interface OptionBlock extends LsBlockBase {
  readonly kind: "option";
  readonly id?: string;
  readonly mode?: "brave" | "safe";
  readonly text?: string;
}

/** `check {` — the D20 check params block (inside a brave option). */
export interface CheckBlock extends LsBlockBase {
  readonly kind: "check";
}

/**
 * A condition-bearing branch:
 *  - body form:  `@if (<cond>) {` / `@else @if (<cond>) {` / `@else {`
 *  - gate form:  `@if (<cond>): @next X` / `@else: @end complete`
 */
export interface IfBlock extends LsBlockBase {
  readonly kind: "if";
  /** The condition text inside `( )`. Absent for a bare `@else`. */
  readonly condition?: string;
  /** True when this branch is an `@else` (with or without a trailing `@if`). */
  readonly isElse: boolean;
  /** Inline gate outcome on the same line: `@next X` or `@end TYPE`. */
  readonly next?: string;
  readonly end?: string;
}

/** `@gate {` — the per-episode routing block. */
export interface GateBlock extends LsBlockBase {
  readonly kind: "gate";
  /** Inline outcome for the minimal `@gate { @next X }` / `@gate { @end T }` form. */
  readonly next?: string;
  readonly end?: string;
}

/** `@affection <char> <+/-N>`. */
export interface AffectionBlock extends LsBlockBase {
  readonly kind: "affection";
  readonly character?: string;
  readonly delta?: string;
}

/** `@signal mark <event>` / `@signal int <name> <op> <value>`. */
export interface SignalBlock extends LsBlockBase {
  readonly kind: "signal";
  readonly signalKind?: "mark" | "int";
  readonly name?: string;
  /** int form only: `=` | `+` | `-`. */
  readonly op?: string;
  /** int form only: the numeric operand. */
  readonly value?: string;
}

/** `@trick <type> "<prompt>"` (leaf). */
export interface TrickBlock extends LsBlockBase {
  readonly kind: "trick";
  readonly trickType?: string;
  readonly prompt?: string;
}

/** `@phone {` — the messaging-UI compound block. */
export interface PhoneBlock extends LsBlockBase {
  readonly kind: "phone";
}

/** `@text from/to <CHAR>: content` — a phone message row. */
export interface PhoneTextBlock extends LsBlockBase {
  readonly kind: "phoneText";
  readonly direction?: "from" | "to";
  readonly character?: string;
  readonly text?: string;
}

/** `@minigame <name> "<description>"` (leaf). */
export interface MinigameBlock extends LsBlockBase {
  readonly kind: "minigame";
  readonly name?: string;
  readonly description?: string;
}

/** `@achievement <id> {` — achievement unlock (compound, carries metadata). */
export interface AchievementBlock extends LsBlockBase {
  readonly kind: "achievement";
  readonly id?: string;
}

/** `@butterfly "<description>"` — downstream memory, not routing. */
export interface ButterflyBlock extends LsBlockBase {
  readonly kind: "butterfly";
  readonly description?: string;
}

/**
 * A `key: value` metadata line inside a compound body — `check { attr / dc }`
 * (D20 检定 params) and `@achievement { name / rarity / description }`. The
 * display parser emits these as editable `param` rows (key + value) so the check
 * / achievement details are editable in card mode instead of falling to `unknown`.
 */
export interface ParamBlock extends LsBlockBase {
  readonly kind: "param";
  readonly key: string;
  /** The verbatim value text after the colon (quotes preserved for round-trip). */
  readonly value: string;
}

/** Anything the display parser cannot classify — preserved verbatim. */
export interface UnknownBlock extends LsBlockBase {
  readonly kind: "unknown";
}

/** The tagged union of every parsed block kind. */
export type LsBlock =
  | EpisodeBlock
  | BgBlock
  | CharBlock
  | DialogueBlock
  | NarrationBlock
  | CgBlock
  | MusicBlock
  | SfxBlock
  | PauseBlock
  | ChoiceBlock
  | OptionBlock
  | CheckBlock
  | IfBlock
  | GateBlock
  | AffectionBlock
  | SignalBlock
  | TrickBlock
  | PhoneBlock
  | PhoneTextBlock
  | MinigameBlock
  | AchievementBlock
  | ButterflyBlock
  | ParamBlock
  | UnknownBlock;

/** Human-facing English label per kind (色盲双编码：色点 + 文字, §4.14 无障碍). */
const KIND_LABEL: Record<LsBlockKind, string> = {
  episode: "Episode",
  bg: "Background",
  char: "Character",
  dialogue: "Dialogue",
  narration: "Narration",
  cg: "CG",
  music: "Music",
  sfx: "Sound",
  pause: "Pause",
  choice: "Choice",
  option: "Option",
  check: "Check",
  if: "Condition",
  gate: "Route",
  affection: "Affection",
  signal: "Signal",
  trick: "Gesture",
  phone: "Phone",
  phoneText: "Message",
  minigame: "Minigame",
  achievement: "Achievement",
  butterfly: "Memory",
  param: "Parameter",
  unknown: "Unknown",
};

/**
 * The `--kind-*` design token that colors a block (§3.7 色表 / §4.14).
 * Returned as the bare token *name* so callers can drop it into
 * `var(${kindToken(kind)})`.
 */
const KIND_TOKEN: Record<LsBlockKind, string> = {
  episode: "--kind-label",
  bg: "--kind-bg",
  char: "--kind-character",
  dialogue: "--kind-dialogue",
  narration: "--kind-dialogue",
  cg: "--kind-cg",
  music: "--kind-music",
  sfx: "--kind-sfx",
  pause: "--kind-pause",
  choice: "--kind-choice",
  option: "--kind-choice",
  check: "--kind-choice",
  if: "--kind-if",
  gate: "--kind-gate",
  affection: "--kind-affection",
  signal: "--kind-signal",
  trick: "--kind-trick",
  phone: "--kind-phone",
  phoneText: "--kind-phone",
  minigame: "--kind-minigame",
  achievement: "--kind-record",
  butterfly: "--kind-record",
  param: "--kind-label",
  unknown: "--kind-label",
};

/** The Chinese display label for a block kind. */
export function kindLabel(kind: LsBlockKind): string {
  return KIND_LABEL[kind];
}

/** The bare `--kind-*` CSS variable name a block kind is colored with. */
export function kindToken(kind: LsBlockKind): string {
  return KIND_TOKEN[kind];
}

// ── line classifiers ──────────────────────────────────────────────────────

/** A SCREAMING dialogue/narration speaker: leading caps token before `:`/`[`. */
const SPEAKER_RE = /^([A-Z][A-Z0-9_]*)(?:\s*\[([^\]]*)\])?\s*:\s*([\s\S]*)$/;

/** Pull the first double-quoted string out of a line. */
function firstQuoted(s: string): string | undefined {
  const m = s.match(/"([\s\S]*?)"/);
  return m ? m[1] : undefined;
}

/** Pull a parenthesised condition out of a line (first `( ... )`, balanced-ish). */
function parenCondition(s: string): string | undefined {
  const open = s.indexOf("(");
  if (open < 0) return undefined;
  let depth = 0;
  for (let i = open; i < s.length; i++) {
    const c = s[i];
    if (c === "(") depth++;
    else if (c === ")") {
      depth--;
      if (depth === 0) return s.slice(open + 1, i).trim();
    }
  }
  return s.slice(open + 1).trim() || undefined;
}

/** Parse a `@next X` / `@end TYPE` trailing outcome from a gate/if line. */
function parseOutcome(s: string): { next?: string; end?: string } {
  const next = s.match(/@next\s+(\S+)/);
  if (next) return { next: next[1] };
  const end = s.match(/@end\s+(\S+)/);
  if (end) return { end: end[1] };
  return {};
}

/** Directive verbs that open a `{ }` body (compound blocks). */
const COMPOUND_OPENERS = new Set(["choice", "phone", "gate", "if", "achievement", "option"]);

/**
 * Parse `.ls` source into a flat, ordered list of display blocks.
 *
 * Never throws. Unrecognised significant lines become `unknown` blocks so the
 * code↔block round-trip stays lossless.
 */
export function parseLsBlocks(source: string): LsBlock[] {
  const blocks: LsBlock[] = [];
  if (typeof source !== "string" || source.length === 0) return blocks;

  const lines = source.split(/\r?\n/);
  let depth = 0;

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i] ?? "";
    const line = rawLine.trim();
    const lineNo = i + 1;

    // skip blanks + comments
    if (line === "" || line.startsWith("//")) continue;

    // A line can both close and open braces (e.g. `} @else {`). Net depth
    // change drives indentation; the block's own depth is computed so that a
    // closing brace de-indents the line it sits on, an opening brace indents
    // what follows.
    const opens = countChar(line, "{");
    const closes = countChar(line, "}");

    // A pure structural brace line carries no directive — don't emit a block,
    // just adjust depth.
    const stripped = line.replace(/[{}]/g, "").trim();
    if (stripped === "") {
      depth = Math.max(0, depth + opens - closes);
      continue;
    }

    // Depth shown for THIS block: if the line begins by closing a brace
    // (`} @else {`, `}`), it belongs to the outer level.
    const startsWithClose = line.startsWith("}");
    const blockDepth = Math.max(0, startsWithClose ? depth - 1 : depth);

    const block = classify(stripped, line, lineNo, blockDepth);
    if (block) blocks.push(block);

    depth = Math.max(0, depth + opens - closes);
  }

  return blocks;
}

function countChar(s: string, ch: string): number {
  let n = 0;
  for (const c of s) if (c === ch) n++;
  return n;
}

/**
 * Classify a single significant line into a block. `stripped` has braces
 * removed (for verb detection); `raw` is the trimmed source line (preserved).
 */
function classify(stripped: string, raw: string, line: number, depth: number): LsBlock | null {
  const base = { raw, line, depth } as const;

  // ── dialogue / narration (no @ prefix) ──
  const speaker = stripped.match(SPEAKER_RE);
  if (speaker) {
    const name = speaker[1]!;
    const pose = speaker[2]?.trim() || undefined;
    const text = speaker[3] ?? "";
    if (name === "NARRATOR" || name === "YOU") {
      return { ...base, kind: "narration", speaker: name, text };
    }
    return { ...base, kind: "dialogue", speaker: name, text, ...(pose ? { pose } : {}) };
  }

  // ── bare `check` keyword block (LS-SPEC §4.6.3 — `check { attr / dc }`, no @ prefix) ──
  if (stripped === "check") return { ...base, kind: "check" };

  // ── `key: value` metadata line (no @ prefix, lowercase-initial key) ──
  // These live inside compound bodies — `check { attr: CHA / dc: 12 }` and
  // `@achievement { name / rarity / description }`. Classifying them as editable
  // `param` rows is what makes 检定 / 成就 details editable in card mode. Must run
  // AFTER the SCREAMING speaker match (dialogue keys are uppercase) so `NARRATOR:`
  // still wins, and the `@`/`&` directive scan below (a param key never starts `@`).
  const param = stripped.match(/^([a-z][A-Za-z0-9_]*)\s*:\s*(\S[\s\S]*)$/);
  if (param) return { ...base, kind: "param", key: param[1]!, value: param[2]!.trim() };

  // ── directives: `@verb …` or concurrent `&verb …` ──
  // The verb is the leading identifier only; anything glued after it (`:` in the
  // gate form `@else: @next X`, `(` in `@if(cond)`, etc.) belongs to `rest`.
  const dir = stripped.match(/^([@&])([A-Za-z_][\w-]*)([\s\S]*)$/);
  if (!dir) return { ...base, kind: "unknown" };

  const concurrent = dir[1] === "&";
  const verb = dir[2]!;
  const rest = (dir[3] ?? "").replace(/^[:\s]+/, "").trim();
  const cc = concurrent ? { concurrent: true } : {};

  switch (verb) {
    case "episode": {
      // @episode <branch_key> "<title>"
      const branchKey = rest.split(/\s+/)[0] || undefined;
      const title = firstQuoted(rest);
      return { ...base, kind: "episode", ...(branchKey ? { branchKey } : {}), ...(title !== undefined ? { title } : {}) };
    }
    case "bg": {
      // @bg set <name> [transition]
      const parts = rest.split(/\s+/).filter(Boolean);
      const idx = parts[0] === "set" ? 1 : 0;
      const name = parts[idx];
      const transition = parts[idx + 1];
      return { ...base, kind: "bg", ...cc, ...(name ? { name } : {}), ...(transition ? { transition } : {}) };
    }
    case "cg": {
      const name = rest.split(/\s+/)[0] || undefined;
      const content = firstQuoted(rest);
      return { ...base, kind: "cg", ...cc, ...(name ? { name } : {}), ...(content !== undefined ? { content } : {}) };
    }
    case "music": {
      if (rest === "stop") return { ...base, kind: "music", ...cc, stop: true };
      const name = rest.split(/\s+/)[0] || undefined;
      return { ...base, kind: "music", ...cc, ...(name ? { name } : {}) };
    }
    case "sfx": {
      const name = rest.split(/\s+/)[0] || undefined;
      return { ...base, kind: "sfx", ...cc, ...(name ? { name } : {}) };
    }
    case "pause":
      return { ...base, kind: "pause", ...cc };
    case "choice":
      return { ...base, kind: "choice" };
    case "phone":
      return { ...base, kind: "phone" };
    case "text": {
      // @text from EASTON: Can we talk?
      const msg = rest.match(/^(from|to)\s+([A-Za-z_][\w-]*)\s*:\s*([\s\S]*)$/);
      if (msg) {
        return {
          ...base,
          kind: "phoneText",
          direction: msg[1] as "from" | "to",
          character: msg[2],
          text: msg[3] ?? "",
        };
      }
      return { ...base, kind: "unknown" };
    }
    case "option": {
      // @option <ID> <brave|safe> "<text>"
      const parts = rest.split(/\s+/).filter(Boolean);
      const id = parts[0] || undefined;
      const mode = parts[1] === "brave" || parts[1] === "safe" ? (parts[1] as "brave" | "safe") : undefined;
      const text = firstQuoted(rest);
      return {
        ...base,
        kind: "option",
        ...(id ? { id } : {}),
        ...(mode ? { mode } : {}),
        ...(text !== undefined ? { text } : {}),
      };
    }
    case "check":
      return { ...base, kind: "check" };
    case "if":
    case "else": {
      // body: @if (c) { | @else @if (c) { | @else {
      // gate: @if (c): @next X | @else: @end T
      const isElse = verb === "else";
      const condition = parenCondition(rest);
      const outcome = parseOutcome(rest);
      return {
        ...base,
        kind: "if",
        isElse,
        ...(condition !== undefined ? { condition } : {}),
        ...(outcome.next ? { next: outcome.next } : {}),
        ...(outcome.end ? { end: outcome.end } : {}),
      };
    }
    case "gate": {
      // minimal inline form: @gate { @next X } / @gate { @end T }
      const outcome = parseOutcome(rest);
      return { ...base, kind: "gate", ...(outcome.next ? { next: outcome.next } : {}), ...(outcome.end ? { end: outcome.end } : {}) };
    }
    case "affection": {
      const parts = rest.split(/\s+/).filter(Boolean);
      const character = parts[0] || undefined;
      const delta = parts[1] || undefined;
      return { ...base, kind: "affection", ...cc, ...(character ? { character } : {}), ...(delta ? { delta } : {}) };
    }
    case "signal": {
      // @signal mark <event> | @signal int <name> <op> <value>
      const parts = rest.split(/\s+/).filter(Boolean);
      const signalKind = parts[0] === "mark" ? "mark" : parts[0] === "int" ? "int" : undefined;
      if (signalKind === "int") {
        const name = parts[1];
        // forms: `name = 0` / `name +1` / `name -2`
        const tail = parts.slice(2).join(" ");
        const eq = tail.match(/^(=)\s*(-?\d+)/);
        const inc = tail.match(/^([+-])\s*(\d+)/);
        const op = eq ? "=" : inc ? inc[1] : undefined;
        const value = eq ? eq[2] : inc ? inc[2] : undefined;
        return {
          ...base,
          kind: "signal",
          ...cc,
          signalKind,
          ...(name ? { name } : {}),
          ...(op ? { op } : {}),
          ...(value !== undefined ? { value } : {}),
        };
      }
      // mark form (also default when kind word is missing/garbled)
      const name = signalKind === "mark" ? parts.slice(1).join(" ").replace(/^"|"$/g, "") || undefined : undefined;
      return {
        ...base,
        kind: "signal",
        ...cc,
        ...(signalKind ? { signalKind } : {}),
        ...(name ? { name } : {}),
      };
    }
    case "trick": {
      const trickType = rest.split(/\s+/)[0] || undefined;
      const prompt = firstQuoted(rest);
      return { ...base, kind: "trick", ...cc, ...(trickType ? { trickType } : {}), ...(prompt !== undefined ? { prompt } : {}) };
    }
    case "minigame": {
      const name = rest.split(/\s+/)[0] || undefined;
      const description = firstQuoted(rest);
      return {
        ...base,
        kind: "minigame",
        ...cc,
        ...(name ? { name } : {}),
        ...(description !== undefined ? { description } : {}),
      };
    }
    case "achievement": {
      const id = rest.split(/\s+/)[0] || undefined;
      return { ...base, kind: "achievement", ...(id ? { id } : {}) };
    }
    case "butterfly": {
      const description = firstQuoted(rest);
      return { ...base, kind: "butterfly", ...cc, ...(description !== undefined ? { description } : {}) };
    }
    default: {
      // Any other `@<word>` is a character presentation directive:
      //   @<char> <pose> [transition]  |  @<char> bubble <type>
      // (LS-SPEC §4.2 — char IDs are lowercase, verb is the char id.)
      const parts = rest.split(/\s+/).filter(Boolean);
      if (parts[0] === "bubble") {
        return { ...base, kind: "char", ...cc, character: verb, ...(parts[1] ? { pose: parts[1], bubble: true } : { bubble: true }) };
      }
      const pose = parts[0];
      const transition = parts[1];
      return {
        ...base,
        kind: "char",
        ...cc,
        character: verb,
        ...(pose ? { pose } : {}),
        ...(transition ? { transition } : {}),
      };
    }
  }
}
